import { EVENT_CATEGORY_LABELS } from '@/constants/eventCategories'
import { deviceLocale, formatInstant, formatTimeOfDay, parseUtcInstant, unixToDate } from '@lib/datetime'
import { formatMoney } from '@lib/money'
import type { EventCategory } from '@orbit/api-types'
import type { ChatAuthor, ChatCard, ChatTagKind } from './types'

/** A card's glyph when it has no category to draw: its kind's; `link` is a Link preview's. */
export type ChatCardGlyph = 'flight' | 'accommodation' | 'idea' | 'expense' | 'payUp' | 'member' | 'link' | 'unknown'

/** One leg of a flight booking: "JL 61 · LAX → HND", "Sun, Mar 14 · 11:50 → 16:25 +1". */
export interface ChatCardLegPresentation {
	route: string
	times: string | null
}

/**
 * What a card says: its title, a glyph, and the key facts its kind has. The stream's
 * cards, the composer's attached cards and the picker's rows all read it. Ported from
 * iOS `ChatCardPresentation`.
 */
export interface ChatCardPresentation {
	/** With "(removed)" after it when the resource is gone. */
	title: string
	glyph: ChatCardGlyph
	/** An activity's or idea's category, drawn in place of the glyph. */
	category: EventCategory | null
	/** One line of facts: an accommodation's dates and guests, an expense's date and amount, an activity's day and time. */
	detail: string | null
	/** An idea's category, as a pill. */
	pill: string | null
	legs: ChatCardLegPresentation[]
	/** What a removed card says when asked; null while the resource is there. */
	removedNotice: string | null
}

export const REMOVED_SUFFIX = ' (removed)'

export const presentCard = (card: ChatCard): ChatCardPresentation => {
	const hasCategory = card.kind === 'TripEvent' || card.kind === 'Idea'
	return {
		title: card.title + (card.removed ? REMOVED_SUFFIX : ''),
		glyph: glyphOf(card.kind),
		category: hasCategory ? card.category : null,
		detail: detailOf(card),
		pill: card.kind === 'Idea' && card.category ? EVENT_CATEGORY_LABELS[card.category] : null,
		legs: card.kind === 'FlightGroup' ? card.legs.map(presentLeg) : [],
		removedNotice: card.removed ? removedNotice(card.kind) : null,
	}
}

const glyphOf = (kind: ChatTagKind): ChatCardGlyph => {
	switch (kind) {
		case 'FlightGroup':
			return 'flight'
		case 'Accommodation':
			return 'accommodation'
		case 'TripEvent':
		case 'Idea':
			return 'idea'
		case 'Expense':
			return 'expense'
		case 'PayUp':
			return 'payUp'
		case 'Member':
			return 'member'
		case 'Other':
			return 'unknown'
	}
}

const removedNotice = (kind: ChatTagKind): string => {
	switch (kind) {
		case 'FlightGroup':
			return 'This flight was removed'
		case 'Accommodation':
			return 'This accommodation was removed'
		case 'TripEvent':
			return 'This activity was removed'
		case 'Idea':
			return 'This idea was removed'
		case 'Expense':
			return 'This expense was removed'
		case 'PayUp':
			return 'This pay-up was removed'
		case 'Member':
			return "They've left the trip"
		case 'Other':
			return 'This was removed'
	}
}

const detailOf = (card: ChatCard): string | null => {
	switch (card.kind) {
		case 'Accommodation':
			return joined([dayRange(card.checkInDate, card.checkOutDate), guests(card.people)])
		case 'Expense':
		case 'PayUp':
			return joined([
				card.date != null ? formatInstant(unixToDate(card.date), 'monthDay') : null,
				card.amount != null ? formatMoney(card.amount, card.currencyCode ?? '') : null,
			])
		case 'TripEvent':
			return joined([card.tripDayIndex != null ? `Day ${card.tripDayIndex + 1}` : null, formatTimeOfDay(card.startTime)])
		default:
			return null
	}
}

/**
 * "Priya", or "Priya + 2 more", the way the create-trip bar lists places: the count
 * stays readable on the one line an attached card or a picker row has.
 */
const guests = (people: ChatAuthor[]): string | null => {
	const [first] = people
	if (!first) return null
	return people.length === 1 ? first.firstName : `${first.firstName} + ${people.length - 1} more`
}

/** "Mar 16 – 19", or "Mar 30 – Apr 2" across months, on the viewer's clock. */
const dayRange = (checkIn: string | null, checkOut: string | null): string | null => {
	if (!checkIn) return null
	const start = parseUtcInstant(checkIn)
	const opening = formatInstant(start, 'monthDay')
	if (!checkOut) return opening
	const end = parseUtcInstant(checkOut)
	const sameMonth = start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth()
	if (sameMonth && start.getDate() === end.getDate()) return opening
	const closing = sameMonth ? end.toLocaleDateString(deviceLocale(), { day: 'numeric' }) : formatInstant(end, 'monthDay')
	return `${opening} – ${closing}`
}

interface WallClock {
	date: Date
	hasTimeOfDay: boolean
}

/** A flight's wall-clock `YYYY-MM-DD[ HH:MM:SS]`, read on its own digits through UTC so nothing shifts. */
const wallClock = (value: string | null): WallClock | null => {
	const match = value?.match(/^(\d{4})-(\d{2})-(\d{2})(?: (\d{2}):(\d{2}):(\d{2}))?$/)
	if (!match) return null
	const [, y, mo, d, h, mi] = match
	return {
		date: new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(h ?? 0), Number(mi ?? 0))),
		hasTimeOfDay: h !== undefined,
	}
}

/** Flights pin the 24-hour aviation clock in every locale (issue #164). */
const clock = (time: WallClock): string =>
	`${String(time.date.getUTCHours()).padStart(2, '0')}:${String(time.date.getUTCMinutes()).padStart(2, '0')}`

const presentLeg = (leg: ChatCard['legs'][number]): ChatCardLegPresentation => {
	const flight = [leg.airlineIata, leg.flightNumber ?? ''].join(' ').trim()
	const airports = [leg.departureAirportCode, leg.arrivalAirportCode].filter(Boolean).join(' → ')
	const route = joined([flight || null, airports || null]) ?? ''

	const departure = wallClock(leg.departureTimeStr)
	if (!departure) return { route, times: null }
	const day = departure.date.toLocaleDateString('en-US', {
		weekday: 'short',
		month: 'short',
		day: 'numeric',
		timeZone: 'UTC',
	})
	if (!departure.hasTimeOfDay) return { route, times: day }
	let times = clock(departure)
	const arrival = wallClock(leg.arrivalTimeStr)
	if (arrival?.hasTimeOfDay) {
		// Whole days between the two dates, on their own digits.
		const days = Math.round((dayStart(arrival.date) - dayStart(departure.date)) / 86_400_000)
		times += ` → ${clock(arrival)}${days > 0 ? ` +${days}` : ''}`
	}
	return { route, times: `${day} · ${times}` }
}

const dayStart = (date: Date): number => Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())

const joined = (parts: (string | null)[]): string | null => {
	const present = parts.filter((part): part is string => !!part)
	return present.length === 0 ? null : present.join(' · ')
}
